import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import ImageUploader from '../components/ImageUploader';
import FileUploader from '../components/FileUploader';
import AttachmentPreview from '../components/AttachmentPreview';
import EditEventModal from '../components/EditEventModal';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const Events = () => {
  const { user } = useContext(AuthContext);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showEventDetails, setShowEventDetails] = useState(false);
  const [showEditEvent, setShowEditEvent] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});
  const [registrationData, setRegistrationData] = useState({});
  const [userRegistrations, setUserRegistrations] = useState({});
  
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    event_type: '',
    start_date: '',
    end_date: '',
    location: '',
    venue: '',
    max_attendees: '',
    price: 0,
    tags: [],
    links: []
  });
  
  // File upload states
  const [bannerImage, setBannerImage] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [eventAttachments, setEventAttachments] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, [filter]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = {};
      
      // Basic status filter
      if (filter !== 'all') {
        params.status_filter = filter;
      }
      
      const response = await axios.get(`${backendUrl}/api/events`, { 
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setEvents(response.data);
      console.log('✅ Events loaded successfully:', response.data.length, 'events');
    } catch (error) {
      console.error('🚨 Error fetching events:', error);
      console.log('🚨 DEBUG: Events fetch failed with:');
      console.log('   Status:', error.response?.status);
      console.log('   Response Data:', error.response?.data);
      
      // Set empty array on error to prevent infinite loading
      setEvents([]);
      
      // Show user-friendly error message
      alert('Failed to load events. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const registerForEvent = async (eventId) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return;

    try {
      const response = await axios.post(`${backendUrl}/api/events/${eventId}/register`, {
        registration_data: {}
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      alert(response.data.message);
      
      // Update user registrations
      setUserRegistrations(prev => ({
        ...prev,
        [eventId]: {
          status: response.data.status,
          qr_code: response.data.qr_code,
          registration_id: response.data.registration_id
        }
      }));

      // Update event attendee count if registered (not waitlisted)
      if (response.data.status === 'registered') {
        setEvents(events.map(event => 
          event.id === eventId 
            ? { ...event, current_attendees: event.current_attendees + 1 }
            : event
        ));
      }
    } catch (error) {
      console.error('Error registering for event:', error);
      alert(error.response?.data?.detail || 'Failed to register for event');
    }
  };

  // Enhanced registration modal for events with custom fields
  const openRegistrationModal = (event) => {
    setSelectedEvent(event);
    setShowRegistrationModal(true);
    setRegistrationData({});
  };

  const closeRegistrationModal = () => {
    setShowRegistrationModal(false);
    setSelectedEvent(null);
    setRegistrationData({});
  };

  // Event details modal functions
  const openEventDetails = async (event) => {
    setSelectedEvent(event);
    setShowEventDetails(true);
    
    // Fetch event attachments
    try {
      const response = await axios.get(`${backendUrl}/api/events/${event.id}/attachments`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setEventAttachments(response.data);
    } catch (error) {
      console.error('Error fetching event attachments:', error);
      setEventAttachments(null);
    }
  };

  const closeEventDetails = () => {
    setShowEventDetails(false);
    setSelectedEvent(null);
    setEventAttachments(null);
  };

  const submitEnhancedRegistration = async () => {
    if (!selectedEvent) return;

    try {
      const response = await axios.post(`${backendUrl}/api/events/${selectedEvent.id}/register`, {
        registration_data: registrationData
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      alert(response.data.message);
      
      // Update user registrations
      setUserRegistrations(prev => ({
        ...prev,
        [selectedEvent.id]: {
          status: response.data.status,
          qr_code: response.data.qr_code,
          registration_id: response.data.registration_id
        }
      }));

      // Update event attendee count if registered (not waitlisted)
      if (response.data.status === 'registered') {
        setEvents(prev => prev.map(event => 
          event.id === selectedEvent.id 
            ? { ...event, current_attendees: event.current_attendees + 1 }
            : event
        ));
      }

      closeRegistrationModal();
    } catch (error) {
      console.error('Error registering for event:', error);
      alert(error.response?.data?.detail || 'Failed to register for event');
    }
  };

  const createEvent = async () => {
    try {
      console.log('🔍 DEBUG: Creating event with data:', newEvent);
      setUploadingFiles(true);
      
      const eventData = {
        ...newEvent,
        start_date: new Date(newEvent.start_date).toISOString(),
        end_date: new Date(newEvent.end_date).toISOString(),
        max_attendees: newEvent.max_attendees ? parseInt(newEvent.max_attendees) : null,
        price: parseFloat(newEvent.price) || 0
      };
      
      console.log('🔍 DEBUG: Processed event data:', eventData);
      
      // Create event first
      const response = await axios.post(`${backendUrl}/api/events`, eventData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const createdEvent = response.data;
      console.log('✅ Event created successfully:', createdEvent);
      
      // Upload files if any
      let uploadResults = {
        banner: null,
        gallery: [],
        attachments: []
      };
      
      try {
        // Upload banner image
        if (bannerImage) {
          console.log('📤 Uploading banner image...');
          const bannerFormData = new FormData();
          bannerFormData.append('file', bannerImage);
          bannerFormData.append('event_id', createdEvent.id);
          bannerFormData.append('is_banner', 'true');
          bannerFormData.append('description', 'Event banner image');
          
          const bannerResponse = await axios.post(`${backendUrl}/api/events/upload/image`, bannerFormData, {
            headers: { 
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          uploadResults.banner = bannerResponse.data;
          console.log('✅ Banner uploaded:', bannerResponse.data);
        }
        
        // Upload gallery images
        if (galleryImages.length > 0) {
          console.log('📤 Uploading gallery images...');
          for (const image of galleryImages) {
            const imageFormData = new FormData();
            imageFormData.append('file', image);
            imageFormData.append('event_id', createdEvent.id);
            imageFormData.append('is_banner', 'false');
            imageFormData.append('description', `Gallery image: ${image.name}`);
            
            const imageResponse = await axios.post(`${backendUrl}/api/events/upload/image`, imageFormData, {
              headers: { 
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'multipart/form-data'
              }
            });
            uploadResults.gallery.push(imageResponse.data);
            console.log('✅ Gallery image uploaded:', imageResponse.data);
          }
        }
        
        // Upload attachment files
        if (attachmentFiles.length > 0) {
          console.log('📤 Uploading attachment files...');
          for (const file of attachmentFiles) {
            const fileFormData = new FormData();
            fileFormData.append('file', file);
            fileFormData.append('event_id', createdEvent.id);
            fileFormData.append('description', `Event attachment: ${file.name}`);
            
            const fileResponse = await axios.post(`${backendUrl}/api/events/upload/attachment`, fileFormData, {
              headers: { 
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'multipart/form-data'
              }
            });
            uploadResults.attachments.push(fileResponse.data);
            console.log('✅ Attachment uploaded:', fileResponse.data);
          }
        }
        
      } catch (uploadError) {
        console.error('⚠️ Some files failed to upload:', uploadError);
        // Event is created, but some files failed
      }
      
      setEvents([createdEvent, ...events]);
      setShowCreateEvent(false);
      
      // Reset form and file states
      setNewEvent({
        title: '',
        description: '',
        event_type: '',
        start_date: '',
        end_date: '',
        location: '',
        venue: '',
        max_attendees: '',
        price: 0,
        tags: [],
        links: []
      });
      setBannerImage(null);
      setGalleryImages([]);
      setAttachmentFiles([]);
      
      // Show success message
      const uploadSummary = [
        uploadResults.banner ? '✅ Banner image' : '',
        uploadResults.gallery.length > 0 ? `✅ ${uploadResults.gallery.length} gallery images` : '',
        uploadResults.attachments.length > 0 ? `✅ ${uploadResults.attachments.length} attachments` : ''
      ].filter(Boolean).join(', ');
      
      alert(`Event created successfully! ${uploadSummary ? `Files uploaded: ${uploadSummary}` : ''}`);
      
    } catch (error) {
      console.error('❌ Error creating event:', error);
      alert(error.response?.data?.detail || 'Failed to create event');
    } finally {
      setUploadingFiles(false);
    }
  };

  // Edit Event Functions
  const canEditEvent = (event) => {
    console.log('🔍 DEBUG canEditEvent - User:', user);
    console.log('🔍 DEBUG canEditEvent - User Role:', user?.role);
    console.log('🔍 DEBUG canEditEvent - Event:', event);
    console.log('🔍 DEBUG canEditEvent - Event Organizer ID:', event.organizer_id);
    
    if (!user) {
      console.log('❌ No user - cannot edit');
      return false;
    }
    
    // Admin and moderators can edit any event
    if (user.role === 'admin' || user.role === 'moderator') {
      console.log('✅ User is admin/moderator - can edit');
      return true;
    }
    
    // Event organizer can edit their own event
    if (event.organizer_id === user.id) {
      console.log('✅ User is event organizer - can edit');
      return true;
    }
    
    console.log('❌ User cannot edit this event');
    return false;
  };

  const openEditEvent = async (event) => {
    if (!canEditEvent(event)) {
      alert('You do not have permission to edit this event');
      return;
    }
    
    try {
      console.log('🔍 Checking permissions for event:', event.id);
      
      // Check permissions first
      const permResponse = await axios.get(`${backendUrl}/api/events/${event.id}/permissions`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      console.log('📋 Permissions received:', permResponse.data);
      
      if (!permResponse.data.can_edit) {
        alert(`Cannot edit event: ${permResponse.data.reason}`);
        return;
      }
      
      setUserPermissions(permResponse.data);
      setEditingEvent(event);
      setShowEditEvent(true);
      
    } catch (error) {
      console.error('❌ Error checking permissions:', error);
      alert('Error checking edit permissions');
    }
  };

  const closeEditEvent = () => {
    setShowEditEvent(false);
    setEditingEvent(null);
    setUserPermissions({});
  };

  // Delete Event Function (Admin only)
  const deleteEvent = async (eventId) => {
    if (!user || user.role !== 'admin') {
      alert('Only administrators can delete events');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${backendUrl}/api/events/${eventId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      alert('Event deleted successfully');
      
      // Close modal and refresh events list
      closeEventDetails();
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      alert(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  const handleSaveEvent = async (updatedEvent) => {
    try {
      console.log('💾 Saving updated event:', updatedEvent);
      
      // Update the event in the local state
      setEvents(prevEvents => 
        prevEvents.map(event => 
          event.id === updatedEvent.id ? updatedEvent : event
        )
      );
      
      // If this was the selected event, update it too
      if (selectedEvent && selectedEvent.id === updatedEvent.id) {
        setSelectedEvent(updatedEvent);
      }
      
      console.log('✅ Event updated in local state');
      
    } catch (error) {
      console.error('❌ Error handling saved event:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate actual event status based on dates
  const getActualEventStatus = (event) => {
    const now = new Date();
    const startDate = new Date(event.start_date);
    const endDate = new Date(event.end_date || event.start_date);
    
    // If cancelled, return cancelled
    if (event.status === 'cancelled') {
      return 'cancelled';
    }
    
    // Compare dates
    if (now < startDate) {
      return 'upcoming';
    } else if (now >= startDate && now <= endDate) {
      return 'ongoing';
    } else {
      return 'completed';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-100 text-blue-800';
      case 'ongoing': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-300 rounded w-1/4"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm p-6">
                <div className="h-6 bg-gray-300 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-gray-300 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-300 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Community Events</h1>
          <p className="text-gray-600">Discover networking events, workshops, and trade shows</p>
        </div>
        <button
          onClick={() => setShowCreateEvent(true)}
          className="mt-4 md:mt-0 bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
        >
          + Create Event
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-8">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="all">All Status</option>
          <option value="upcoming">Upcoming</option>
          <option value="ongoing">Ongoing</option>
          <option value="completed">Completed</option>
        </select>
        
        <select className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500">
          <option value="">All Categories</option>
          <option value="workshop">Workshop</option>
          <option value="trade_show">Trade Show</option>
          <option value="networking">Networking</option>
          <option value="conference">Conference</option>
          <option value="webinar">Webinar</option>
        </select>
      </div>

      {/* Events List */}
      <div className="space-y-6">
        {(() => {
          console.log('🔍 DEBUG: About to render events. Events array:', events);
          console.log('🔍 DEBUG: Events array length:', events.length);
          console.log('🔍 DEBUG: Current user in render:', user);
          console.log('🔍 DEBUG: Events array type:', typeof events);
          console.log('🔍 DEBUG: Is events array?', Array.isArray(events));
          return null;
        })()}
        {events.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-500 text-lg mb-4">No events found</p>
            <p className="text-gray-400">Be the first to create an event for the community!</p>
          </div>
        ) : (
          events
            .filter(event => {
              // Filter by calculated status
              if (filter === 'all') return true;
              return getActualEventStatus(event) === filter;
            })
            .sort((a, b) => {
              // Sort by start_date in descending order (most recent first)
              const dateA = new Date(a.start_date);
              const dateB = new Date(b.start_date);
              return dateB - dateA;
            })
            .map((event) => {
            console.log('🎨 Rendering event card for:', event.title);
            console.log('🔍 Current user for event rendering:', user);
            
            return (
            <div key={event.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden">
              {/* Event Banner Image */}
              {event.banner_image_url && (
                <div className="w-full h-48 bg-gray-100 overflow-hidden">
                  <img
                    src={event.banner_image_url.startsWith('http') ? event.banner_image_url : `${backendUrl}${event.banner_image_url}`}
                    alt={event.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.warn(`Failed to load banner image for event ${event.id}:`, event.banner_image_url);
                      e.target.style.display = 'none';
                      // Hide the entire banner container if image fails
                      e.target.parentElement.style.display = 'none';
                    }}
                    onLoad={() => {
                      console.log(`✅ Successfully loaded banner image for event: ${event.title}`);
                    }}
                  />
                </div>
              )}
              
              <div className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
                  {/* Event Info */}
                  <div className="flex-1">
                    <div className="flex items-start space-x-4">
                      <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-lg flex flex-col items-center justify-center font-bold">
                        <div className="text-xs">{formatDate(event.start_date).split(' ')[1]}</div>
                        <div className="text-lg">{formatDate(event.start_date).split(' ')[2]}</div>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-xl font-semibold text-gray-900">{event.title}</h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(getActualEventStatus(event))}`}>
                            {getActualEventStatus(event)}
                          </span>
                        </div>
                        
                        <div className="text-gray-600 mb-3">
                          <span className="font-medium">{event.organizer_name}</span>
                          <span className="mx-2">•</span>
                          <span>{event.event_type}</span>
                        </div>
                        
                        <p className="text-gray-700 mb-4">{event.description}</p>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-4">
                          <div className="flex items-center space-x-1">
                            <span>📅</span>
                            <span>{formatDate(event.start_date)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span>🕒</span>
                            <span>{formatTime(event.start_date)} - {formatTime(event.end_date)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span>📍</span>
                            <span>{event.location}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <span>👥</span>
                            <span>{event.current_attendees}/{event.max_attendees || '∞'} attendees</span>
                          </div>
                        </div>
                        
                        {event.venue && (
                          <div className="text-sm text-gray-600 mb-4">
                            <span className="font-medium">Venue:</span> {event.venue}
                          </div>
                        )}
                        
                        {event.tags && event.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {event.tags.map((tag, index) => (
                              <span key={index} className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Event Links */}
                        {event.links && event.links.length > 0 && (
                          <div className="mb-4">
                            <div className="flex flex-wrap gap-2">
                              {event.links.filter(link => link.title && link.url).map((link, index) => (
                                <a
                                  key={index}
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors space-x-1"
                                >
                                  <span>🔗</span>
                                  <span>{link.title}</span>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Event Actions */}
                  <div className="mt-6 lg:mt-0 lg:ml-6 flex flex-col items-end space-y-3">
                    {event.price > 0 && (
                      <div className="text-right">
                        <div className="text-lg font-bold text-green-600">
                          Rp {event.price.toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-500">per person</div>
                      </div>
                    )}
                    
                    {event.price === 0 && (
                      <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                        Free
                      </div>
                    )}
                    
                    <div className="flex space-x-3">
                      <button 
                        onClick={() => openEventDetails(event)}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        View Details
                      </button>
                      
                      {/* Edit Button for authorized users */}
                      {canEditEvent(event) && (
                        <button
                          onClick={() => openEditEvent(event)}
                          className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center space-x-1"
                          title="Edit Event"
                        >
                          <span>✏️</span>
                          <span>Edit</span>
                        </button>
                      )}
                      
                      {/* Registration Button with Status */}
                      {userRegistrations[event.id] ? (
                        <div className="flex items-center space-x-2">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            userRegistrations[event.id].status === 'registered' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {userRegistrations[event.id].status === 'registered' ? '✓ Registered' : '⏳ Waitlisted'}
                          </span>
                          {userRegistrations[event.id].qr_code && (
                            <button 
                              onClick={() => alert(`QR Code: ${userRegistrations[event.id].qr_code}`)}
                              className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm hover:bg-blue-200 transition-colors"
                            >
                              📱 QR Code
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => registerForEvent(event.id)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                            disabled={event.max_attendees && event.current_attendees >= event.max_attendees}
                          >
                            Quick Register
                          </button>
                          <button
                            onClick={() => openRegistrationModal(event)}
                            className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors disabled:border-gray-400 disabled:text-gray-400 disabled:cursor-not-allowed"
                            disabled={event.max_attendees && event.current_attendees >= event.max_attendees}
                          >
                            {event.max_attendees && event.current_attendees >= event.max_attendees ? 'Event Full' : 'Register with Details'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
          })
        )}
      </div>

      {/* Create Event Modal */}
      {showCreateEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-2xl font-bold text-gray-900">Create New Event</h2>
              <button
                onClick={() => {
                  setShowCreateEvent(false);
                  // Reset form and file states when closing
                  setNewEvent({
                    title: '',
                    description: '',
                    event_type: '',
                    start_date: '',
                    end_date: '',
                    location: '',
                    venue: '',
                    max_attendees: '',
                    price: 0,
                    tags: [],
                    links: []
                  });
                  setBannerImage(null);
                  setGalleryImages([]);
                  setAttachmentFiles([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Title *
                  </label>
                  <input
                    type="text"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter event title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Describe your event"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Event Type *
                    </label>
                    <select
                      value={newEvent.event_type}
                      onChange={(e) => setNewEvent({...newEvent, event_type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select type</option>
                      <option value="Workshop">Workshop</option>
                      <option value="Trade Show">Trade Show</option>
                      <option value="Networking">Networking</option>
                      <option value="Conference">Conference</option>
                      <option value="Webinar">Webinar</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Attendees
                    </label>
                    <input
                      type="number"
                      value={newEvent.max_attendees}
                      onChange={(e) => setNewEvent({...newEvent, max_attendees: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Leave empty for unlimited"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date & Time *
                    </label>
                    <input
                      type="datetime-local"
                      value={newEvent.start_date}
                      onChange={(e) => setNewEvent({...newEvent, start_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date & Time *
                    </label>
                    <input
                      type="datetime-local"
                      value={newEvent.end_date}
                      onChange={(e) => setNewEvent({...newEvent, end_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Location *
                    </label>
                    <input
                      type="text"
                      value={newEvent.location}
                      onChange={(e) => setNewEvent({...newEvent, location: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="City, Indonesia"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Venue
                    </label>
                    <input
                      type="text"
                      value={newEvent.venue}
                      onChange={(e) => setNewEvent({...newEvent, venue: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Specific venue name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price (IDR)
                  </label>
                  <input
                    type="number"
                    value={newEvent.price}
                    onChange={(e) => setNewEvent({...newEvent, price: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0 for free events"
                  />
                </div>

                {/* Event Links */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    🔗 Event Links (Optional)
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Add related links like registration pages, websites, or social media.
                  </p>
                  <div className="space-y-3">
                    {newEvent.links.map((link, index) => (
                      <div key={index} className="flex space-x-2 items-start">
                        <div className="flex-1">
                          <input
                            type="text"
                            placeholder="Link title (e.g., 'Register Here', 'Event Website')"
                            value={link.title}
                            onChange={(e) => {
                              const updatedLinks = [...newEvent.links];
                              updatedLinks[index].title = e.target.value;
                              setNewEvent({...newEvent, links: updatedLinks});
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                          />
                        </div>
                        <div className="flex-1">
                          <input
                            type="url"
                            placeholder="https://example.com"
                            value={link.url}
                            onChange={(e) => {
                              const updatedLinks = [...newEvent.links];
                              updatedLinks[index].url = e.target.value;
                              setNewEvent({...newEvent, links: updatedLinks});
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const updatedLinks = newEvent.links.filter((_, i) => i !== index);
                            setNewEvent({...newEvent, links: updatedLinks});
                          }}
                          className="px-3 py-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          🗑️
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setNewEvent({
                          ...newEvent,
                          links: [...newEvent.links, { title: '', url: '', description: '' }]
                        });
                      }}
                      className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-300 hover:text-blue-500 transition-colors flex items-center justify-center space-x-2"
                    >
                      <span>➕</span>
                      <span>Add Link</span>
                    </button>
                  </div>
                </div>

                {/* File Upload Sections */}
                <div className="space-y-6 border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-medium text-gray-900">📎 Event Media & Files</h3>
                  
                  {/* Banner Image Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      🖼️ Event Banner Image (Recommended)
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Upload a banner image that will represent your event. Recommended size: 1200x600px
                    </p>
                    <ImageUploader
                      onUpload={(files) => setBannerImage(files[0])}
                      multiple={false}
                      maxFiles={1}
                      isUploading={uploadingFiles}
                    />
                  </div>

                  {/* Gallery Images Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      📸 Event Gallery (Optional)
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Upload additional images to showcase your event. Maximum 5 images.
                    </p>
                    <ImageUploader
                      onUpload={(files) => setGalleryImages(prev => [...prev, ...files])}
                      multiple={true}
                      maxFiles={5}
                      isUploading={uploadingFiles}
                    />
                  </div>

                  {/* File Attachments Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      📎 Event Resources & Attachments (Optional)
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Upload documents like agenda, presentations, forms, etc. Maximum 10 files.
                    </p>
                    <FileUploader
                      onUpload={(files) => setAttachmentFiles(prev => [...prev, ...files])}
                      maxFiles={10}
                      isUploading={uploadingFiles}
                    />
                  </div>
                  
                  {/* Upload Summary */}
                  {(bannerImage || galleryImages.length > 0 || attachmentFiles.length > 0) && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2">📋 Upload Summary</h4>
                      <div className="space-y-1 text-sm text-gray-600">
                        {bannerImage && <p>• Banner image: {bannerImage.name}</p>}
                        {galleryImages.length > 0 && <p>• Gallery images: {galleryImages.length} files</p>}
                        {attachmentFiles.length > 0 && <p>• Attachments: {attachmentFiles.length} files</p>}
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => {
                      setShowCreateEvent(false);
                      // Reset form and file states when canceling
                      setNewEvent({
                        title: '',
                        description: '',
                        event_type: '',
                        start_date: '',
                        end_date: '',
                        location: '',
                        venue: '',
                        max_attendees: '',
                        price: 0,
                        tags: [],
                        links: []
                      });
                      setBannerImage(null);
                      setGalleryImages([]);
                      setAttachmentFiles([]);
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    disabled={uploadingFiles}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createEvent}
                    disabled={uploadingFiles || !newEvent.title || !newEvent.description || !newEvent.event_type || !newEvent.start_date || !newEvent.end_date || !newEvent.location}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {uploadingFiles ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Creating & Uploading...</span>
                      </>
                    ) : (
                      <span>Create Event</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="border-t border-gray-200 p-6 flex-shrink-0">
              <div className="text-xs text-gray-500 text-center">
                💡 Tip: Scroll up to see all form sections including image and file uploads
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Registration Modal */}
      {showRegistrationModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Register for Event</h2>
                <button
                  onClick={closeRegistrationModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">{selectedEvent.title}</h3>
                <p className="text-sm text-gray-600 mb-2">{selectedEvent.description}</p>
                <div className="text-sm text-gray-500">
                  <p>📅 {formatDate(selectedEvent.start_date)} at {formatTime(selectedEvent.start_date)}</p>
                  <p>📍 {selectedEvent.location}</p>
                  {selectedEvent.price > 0 && (
                    <p>💰 {selectedEvent.currency} {selectedEvent.price.toLocaleString()}</p>
                  )}
                </div>
              </div>

              {/* Registration Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dietary Restrictions (Optional)
                  </label>
                  <input
                    type="text"
                    value={registrationData.dietary_restrictions || ''}
                    onChange={(e) => setRegistrationData(prev => ({
                      ...prev,
                      dietary_restrictions: e.target.value
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Vegetarian, Halal, No nuts"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company/Organization (Optional)
                  </label>
                  <input
                    type="text"
                    value={registrationData.company || ''}
                    onChange={(e) => setRegistrationData(prev => ({
                      ...prev,
                      company: e.target.value
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Your company name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number (Optional)
                  </label>
                  <input
                    type="tel"
                    value={registrationData.phone || ''}
                    onChange={(e) => setRegistrationData(prev => ({
                      ...prev,
                      phone: e.target.value
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+62 xxx xxxx xxxx"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    value={registrationData.notes || ''}
                    onChange={(e) => setRegistrationData(prev => ({
                      ...prev,
                      notes: e.target.value
                    }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Any special requirements or questions"
                  />
                </div>

                {/* Capacity Warning */}
                {selectedEvent.max_attendees && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-center">
                      <span className="text-yellow-600 mr-2">⚠️</span>
                      <div>
                        <p className="text-sm text-yellow-800">
                          <strong>Capacity:</strong> {selectedEvent.current_attendees || 0} / {selectedEvent.max_attendees} registered
                        </p>
                        {selectedEvent.current_attendees >= selectedEvent.max_attendees && (
                          <p className="text-sm text-yellow-800">
                            Event is full. You will be added to the waitlist.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex space-x-3 pt-6">
                <button
                  onClick={closeRegistrationModal}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitEnhancedRegistration}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {selectedEvent.max_attendees && selectedEvent.current_attendees >= selectedEvent.max_attendees 
                    ? 'Join Waitlist' 
                    : 'Register Now'
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Event Details Modal */}
      {showEventDetails && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedEvent.title}</h2>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium">
                      {selectedEvent.event_type || 'General'}
                    </span>
                    <span>📅 {new Date(selectedEvent.start_date).toLocaleDateString()}</span>
                    <span>📍 {selectedEvent.location || 'TBA'}</span>
                  </div>
                </div>
                <button
                  onClick={closeEventDetails}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ✕
                </button>
              </div>

              {/* Event Details Content */}
              <div className="space-y-6">
                {/* Description */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">📝 Description</h3>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {selectedEvent.description || 'No description provided.'}
                  </p>
                </div>

                {/* Event Information Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Date & Time */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">🗓️ Date & Time</h4>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p><span className="font-medium">Start:</span> {new Date(selectedEvent.start_date).toLocaleString()}</p>
                      {selectedEvent.end_date && (
                        <p><span className="font-medium">End:</span> {new Date(selectedEvent.end_date).toLocaleString()}</p>
                      )}
                    </div>
                  </div>

                  {/* Location & Venue */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">📍 Location</h4>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p><span className="font-medium">Location:</span> {selectedEvent.location || 'TBA'}</p>
                      {selectedEvent.venue && (
                        <p><span className="font-medium">Venue:</span> {selectedEvent.venue}</p>
                      )}
                    </div>
                  </div>

                  {/* Attendance */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">👥 Attendance</h4>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p><span className="font-medium">Current:</span> {selectedEvent.current_attendees || 0} registered</p>
                      {selectedEvent.max_attendees && (
                        <p><span className="font-medium">Capacity:</span> {selectedEvent.max_attendees} maximum</p>
                      )}
                      {selectedEvent.max_attendees && (
                        <div className="mt-2">
                          <div className="bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ 
                                width: `${Math.min(((selectedEvent.current_attendees || 0) / selectedEvent.max_attendees) * 100, 100)}%` 
                              }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {selectedEvent.max_attendees - (selectedEvent.current_attendees || 0)} spots remaining
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Pricing */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">💰 Pricing</h4>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p><span className="font-medium">Price:</span> 
                        {selectedEvent.price > 0 ? `$${selectedEvent.price}` : 'Free'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tags */}
                {selectedEvent.tags && selectedEvent.tags.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">🏷️ Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedEvent.tags.map((tag, index) => (
                        <span 
                          key={index}
                          className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Event Media & Attachments */}
                {eventAttachments && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">📎 Event Media & Resources</h4>
                    <AttachmentPreview 
                      attachments={eventAttachments} 
                      showDelete={false}
                    />
                  </div>
                )}

                {/* Event Status */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">📊 Event Status</h4>
                  <div className="flex items-center space-x-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      selectedEvent.max_attendees && selectedEvent.current_attendees >= selectedEvent.max_attendees
                        ? 'bg-red-100 text-red-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {selectedEvent.max_attendees && selectedEvent.current_attendees >= selectedEvent.max_attendees
                        ? 'Event Full'
                        : 'Registration Open'
                      }
                    </span>
                    
                    {/* Registration Status for Current User */}
                    {userRegistrations[selectedEvent.id] && (
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        ✅ You're Registered
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200 mt-6">
                <button
                  onClick={closeEventDetails}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
                
                {/* Admin Delete Button */}
                {user && user.role === 'admin' && (
                  <button
                    onClick={() => deleteEvent(selectedEvent.id)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
                  >
                    <span>🗑️</span>
                    <span>Delete Event</span>
                  </button>
                )}
                
                {/* Registration Action */}
                {userRegistrations[selectedEvent.id] ? (
                  <div className="flex-1 px-4 py-2 bg-green-50 border border-green-200 text-green-800 rounded-lg text-center font-medium">
                    ✅ Already Registered
                  </div>
                ) : (
                  <div className="flex-1 flex space-x-2">
                    <button
                      onClick={() => {
                        closeEventDetails();
                        openRegistrationModal(selectedEvent);
                      }}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                      disabled={selectedEvent.max_attendees && selectedEvent.current_attendees >= selectedEvent.max_attendees}
                    >
                      {selectedEvent.max_attendees && selectedEvent.current_attendees >= selectedEvent.max_attendees
                        ? 'Event Full'
                        : 'Register Now'
                      }
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      <EditEventModal
        isOpen={showEditEvent}
        onClose={closeEditEvent}
        event={editingEvent}
        onSave={handleSaveEvent}
        userPermissions={userPermissions}
      />
    </div>
  );
};

export default Events;